import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import "./../../components/CustomComponents/Switchery/ToggleSwitch.css";
import swal from "sweetalert";
import "../../components/CustomComponents/footer/footer.css";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
} from "../../app/itemGridPreferencesSlice";
import {
  mergeVisibleColumns,
  needsPreferencesUpdate,
} from "../../utils/preferencesUtils";
import ItemAssignmentModal from "../../components/CustomComponents/modal/ItemAssignmentModal";
import useIsChildBranch from "../../hooks/useIsChildBranch";
import "./../order/ManageOrderFilters.css";

const SEARCH_DEBOUNCE_MS = 1000;

function ManageModifierGroups() {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [modifierGroups, setModifierGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const lastSelectedIndex = useRef(null);
  const [showItemAssignmentModal, setShowItemAssignmentModal] = useState(false);
  const [
    selectedModifierGroupForAssignment,
    setSelectedModifierGroupForAssignment,
  ] = useState(null);
  const [showBulkItemAssignmentModal, setShowBulkItemAssignmentModal] =
    useState(false);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();
  const isChildBranch = useIsChildBranch();



  const pageKey = "modifierGroups";
  const defaultVisibleColumns = {
    name: true,
    subTitle: true,
    minSelection: true,
    maxSelection: true,
    freeSelection: true,
    canSkip: true,
  };
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "Name", label: "Name" };
  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = mergeVisibleColumns(
    preferences.visibleColumns,
    defaultVisibleColumns
  );
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const dispatch = useDispatch();

  const [sortByOptions, setSortByOptions] = useState([
    { value: "Name", label: "Name" },
    { value: "Price", label: "Price" },
  ]);

  const clearFilters = () => {
    setSearch("");
  };

  // Add the renderNoDataMessage function
  const renderNoDataMessage = () => {
    if (search.trim() && modifierGroups.length === 0) {
      return (
        <div className="alert alert-info text-center">
          No modifier groups found matching your search criteria.
        </div>
      );
    }
    return null;
  };

  const handleDelete = async (modifier) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${modifier.name}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await axiosInstance
        .delete(APIs.DELETE_MODIFIER_GROUP, {
          params: {
            CompID,
            ModifierGroupID: modifier.modifierGroupID,
          },
        })
        .then((response) => {
          if (response.data.status) {
            setModifierGroups((prevMenu) =>
              prevMenu.filter(
                (item) => item.modifierGroupID !== modifier.modifierGroupID
              )
            );
            showSuccessToast("Modifier Group is Deleted.");
          } else {
            showErrorToast(response.data.message || "Deletion failed");
          }
        });
    } catch (err) {
      showErrorToast("Something Went Wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (modifierGroupID, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (modifierGroups[i]?.modifierGroupID) {
            newSelected.add(modifierGroups[i].modifierGroupID);
          }
        }
      } else {
        if (newSelected.has(modifierGroupID)) {
          newSelected.delete(modifierGroupID);
        } else {
          newSelected.add(modifierGroupID);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(
        modifierGroups.map((item) => item.modifierGroupID)
      );
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No modifier groups selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected modifier groups?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((modifierGroupID) =>
          axiosInstance.delete(APIs.DELETE_MODIFIER_GROUP, {
            params: {
              CompID,
              ModifierGroupID: modifierGroupID,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchModifierGroupsData();
      showSuccessToast(
        `${selectedItems.size} modifier groups deleted successfully.`
      );
    } catch (err) {
      // Error in bulk delete silently
      showErrorToast("Failed to delete selected modifier groups.");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No modifier groups selected for editing.");
      return;
    }

    const selectedModifierGroupIds = Array.from(selectedItems);
    navigate(`/Add-ons/bulkEdit`, { state: { selectedModifierGroupIds } });
  };

  const handleBulkStatusChange = async (canSkipStatus) => {
    if (selectedItems.size === 0) {
      showErrorToast("No modifier groups selected for status change.");
      return;
    }

    try {
      const confirmStatusChange = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to ${canSkipStatus ? "enable" : "disable"
          } skip for ${selectedItems.size} selected modifier groups?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmStatusChange) return;

      setLoading(true);
      const updatedModifierGroups = modifierGroups.map((group) =>
        selectedItems.has(group.modifierGroupID)
          ? { ...group, canSkip: canSkipStatus }
          : group
      );
      setModifierGroups(updatedModifierGroups);

      await Promise.all(
        Array.from(selectedItems).map((modifierGroupID) =>
          axiosInstance.put(APIs.POST_CHANGE_SKIP_ABLE, {
            CompID,
            ModifierGroupID: modifierGroupID,
            canSkip: canSkipStatus,
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      showSuccessToast(
        `${selectedItems.size} modifier groups status updated successfully.`
      );
    } catch (error) {
      // Error in bulk status change silently
      showErrorToast("Failed to update selected modifier groups status.");
      fetchModifierGroupsData();
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    } finally {
      setLoading(false);
    }
  };

  const toggleSkipAble = async (modifier) => {
    try {
      setLoading(true);
      await axiosInstance.put(APIs.POST_CHANGE_SKIP_ABLE, {
        CompID,
        ModifierGroupID: modifier.modifierGroupID,
        canSkip: !modifier.canSkip,
      });

      setModifierGroups((prevGroups) =>
        prevGroups.map((group) =>
          group.modifierGroupID === modifier.modifierGroupID
            ? { ...group, canSkip: !group.canSkip }
            : group
        )
      );
    } catch (err) {
      showErrorToast("Failed to update modifier group status.");
    } finally {
      setLoading(false);
    }
  };

  const fetchModifierGroupsData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_MODIFIER_GROUPS, {
        params: {
          CompID,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search?.trim() || "",
        },
      });

      const MGroupsData = response.data.data || response.data;
      if (Array.isArray(MGroupsData)) {
        setModifierGroups(MGroupsData);
      } else {
        // Unexpected API response format silently
        setModifierGroups([]);
      }
    } catch (err) {
      // Error fetching modifier groups data silently
      setModifierGroups([]);
    } finally {
      setLoading(false);
    }
  }, [search, sortBy, sortOrder, CompID]);

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (
        needsPreferencesUpdate(
          preferences.visibleColumns,
          defaultVisibleColumns
        )
      ) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(
          preferences.visibleColumns,
          defaultVisibleColumns
        );
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    // eslint-disable-next-line
  }, [dispatch, pageKey]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchModifierGroupsData();
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [fetchModifierGroupsData]);

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Add-on Groups</li>
          </ol>
          <h1 className="page-header mb-0">Manage Add-on Groups</h1>
        </div>
        <div className="ms-auto">
          {!isChildBranch && (
            <button
              className="btn px-4"
              onClick={() => {
                navigate("/Add-ons/addModifierGroup");
              }}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: "none",
                color: "white",
                borderRadius: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "600",
                transition: "all 0.3s ease",
                boxShadow: `0 2px 8px ${themeColor}40`,
              }}
              onMouseEnter={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
              Create Add-on Group
            </button>
          )}
        </div>
      </div>
      <div className="mb-3 mt-2">
        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              <div className="mb-4">
                <div
                  className="card filter-card"
                  style={{
                    position: "relative",
                    zIndex: 1,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: `0 4px 20px ${themeColor}40`
                  }}
                >
                  <div className="card-body p-3">
                    {/* Filter Controls */}
                    <div className="row g-3 align-items-center">
                      {/* Search Field */}
                      <div className="col-xl-4 col-lg-4 col-md-6">
                        <div className="search-input filter-control">
                          <input
                            type="text"
                            className="filter-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by name..."
                            style={{
                              backgroundColor: isDarkMode ? 'rgba(45, 52, 54, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                              border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(255, 255, 255, 0.3)',
                              color: isDarkMode ? '#ffffff' : '#333'
                            }}
                          />
                        </div>
                      </div>

                      {/* Sort By Dropdown */}
                      <div className="col-xl-3 col-lg-3 col-md-6">
                        <div className="filter-control">
                          <Select
                            value={sortBy}
                            onChange={(val) =>
                              dispatch(setSortBy({ pageKey, sortBy: val }))
                            }
                            options={sortByOptions}
                            styles={dropdownStyles}
                            placeholder="Sort By"
                            className="filter-dropdown"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuShouldBlockScroll={false}
                            menuShouldScrollIntoView={false}
                            closeMenuOnScroll={false}
                          />
                        </div>
                      </div>

                      {/* Sort Order Dropdown */}
                      <div className="col-xl-2 col-lg-2 col-md-6">
                        <div className="filter-control">
                          <SortOrderDropDownSelection
                            defaultValue={sortOrder}
                            onChange={(val) =>
                              dispatch(
                                setSortOrder({ pageKey, sortOrder: val })
                              )
                            }
                          />
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="col-xl-3 col-lg-3 col-md-12">
                        <div className="d-flex gap-2">
                          <button
                            className="clear-btn"
                            onClick={clearFilters}
                            title="Clear all filters"
                          >
                            <i className="fa fa-times me-1"></i>
                            Filter
                          </button>

                          <ColumnSelection
                            visibleColumns={visibleColumns}
                            setVisibleColumns={(cols) =>
                              dispatch(
                                setVisibleColumns({ pageKey, columns: cols })
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {selectedItems.size > 0 && (
                <div className="mb-3">
                  <div
                    className="p-3 bg-light rounded d-flex align-items-center justify-content-between"
                    style={{ border: "1px solid #dee2e6" }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <span className="fw-bold text-primary">
                        {selectedItems.size} group
                        {selectedItems.size > 1 ? "s" : ""} selected
                      </span>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleDeselectAll}
                      >
                        Clear Selection
                      </button>
                    </div>

                    <div className="d-flex gap-2">
                      {!isChildBranch && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={handleBulkEdit}
                          title="Edit Selected Groups"
                        >
                          <i className="fa fa-edit me-1"></i>
                          Edit
                        </button>
                      )}
                      {/*  
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => setShowBulkItemAssignmentModal(true)}
                        title="Assign Items to Selected Groups"
                      >
                        <i className="fa fa-plus me-1"></i>
                        Assign Items
                      </button>
                      */}

                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => handleBulkStatusChange(true)}
                        title="Enable Skip for Selected Groups"
                      >
                        <i className="fa fa-check-circle me-1"></i>
                        Enable Skip
                      </button>
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => handleBulkStatusChange(false)}
                        title="Disable Skip for Selected Groups"
                      >
                        <i className="fa fa-times-circle me-1"></i>
                        Disable Skip
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={handleBulkDelete}
                        title="Delete Selected Groups"
                      >
                        <i className="fa fa-trash me-1"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Add the No Data Message */}
              {renderNoDataMessage()}

              {Array.isArray(modifierGroups) && modifierGroups.length > 0 && (
                <div
                  className="table-responsive mt-3"
                  style={{
                    minHeight: "200px",
                    maxHeight: "450px",
                    overflowY: "auto",
                    overflowX: "auto",
                  }}
                >
                  <table className="table table-hover">
                    <thead
                      className="bg-light"
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }}
                    >
                      <tr>
                        <th className="pt-2 pb-2">
                          <input
                            type="checkbox"
                            onChange={handleSelectAll}
                            checked={
                              modifierGroups.length > 0 &&
                              selectedItems.size === modifierGroups.length
                            }
                          />
                        </th>
                        {visibleColumns.name && (
                          <th className="pt-2 pb-2 text-center">Name</th>
                        )}
                        {visibleColumns.subTitle && (
                          <th className="pt-2 pb-2 text-center">Sub Title</th>
                        )}
                        {visibleColumns.freeSelection && (
                          <th className="pt-2 pb-2 text-center">
                            Free Selection
                          </th>
                        )}
                        {visibleColumns.minSelection && (
                          <th className="pt-2 pb-2 text-center">
                            Minimum Selection
                          </th>
                        )}
                        {visibleColumns.maxSelection && (
                          <th className="pt-2 pb-2 text-center">
                            Maximum Selection
                          </th>
                        )}
                        {visibleColumns.canSkip && (
                          <th className="pt-2 pb-2 text-center">Skip Able</th>
                        )}
                        <th className="pt-2 pb-2 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modifierGroups.map((modifier, index) => (
                        <tr key={modifier.modifierGroupID}>
                          <td className="align-middle">
                            <input
                              type="checkbox"
                              checked={selectedItems.has(
                                modifier.modifierGroupID
                              )}
                              onChange={(e) =>
                                handleSelectItem(
                                  modifier.modifierGroupID,
                                  index,
                                  e
                                )
                              }
                              data-chkbox-shiftsel="type1"
                            />
                          </td>
                          {visibleColumns.name && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                if (isChildBranch) return;
                                navigate(`/Add-ons/updateModifierGroup/${modifier.modifierGroupID}`);
                              }}
                              style={{ cursor: isChildBranch ? "default" : "pointer" }}
                            >
                              {modifier.name}
                            </td>
                          )}
                          {visibleColumns.subTitle && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                if (isChildBranch) return;
                                navigate(`/Add-ons/updateModifierGroup/${modifier.modifierGroupID}`);
                              }}
                              style={{ cursor: isChildBranch ? "default" : "pointer" }}
                            >
                              {modifier.subTitle}
                            </td>
                          )}
                          {visibleColumns.freeSelection && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                if (isChildBranch) return;
                                navigate(`/Add-ons/updateModifierGroup/${modifier.modifierGroupID}`);
                              }}
                              style={{ cursor: isChildBranch ? "default" : "pointer" }}
                            >
                              {modifier.freeSelection}
                            </td>
                          )}
                          {visibleColumns.minSelection && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                if (isChildBranch) return;
                                navigate(`/Add-ons/updateModifierGroup/${modifier.modifierGroupID}`);
                              }}
                              style={{ cursor: isChildBranch ? "default" : "pointer" }}
                            >
                              {modifier.minimumSelection}
                            </td>
                          )}
                          {visibleColumns.maxSelection && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                if (isChildBranch) return;
                                navigate(`/Add-ons/updateModifierGroup/${modifier.modifierGroupID}`);
                              }}
                              style={{ cursor: isChildBranch ? "default" : "pointer" }}
                            >
                              {modifier.maximumSelection}
                            </td>
                          )}
                          {visibleColumns.canSkip && (
                            <td className="align-middle text-center">
                              <div
                                className={`toggle-switch ${modifier.canSkip ? "active" : ""
                                  }`}
                              >
                                <label className="switch">
                                  <input
                                    type="checkbox"
                                    checked={modifier.canSkip}
                                    onChange={() => toggleSkipAble(modifier)}
                                  />
                                  <span
                                    className="slider round"
                                    style={{
                                      backgroundColor: modifier.canSkip
                                        ? themeColor
                                        : "#ccc",
                                    }}
                                  ></span>
                                </label>
                              </div>
                            </td>
                          )}
                          <td className="align-middle text-center">
                            <div className="d-flex justify-content-center gap-2" onClick={(e) => e.stopPropagation()}>
                              {/* Edit Icon */}
                              {!isChildBranch && (
                                <Link
                                  to={`/Add-ons/updateModifierGroup/${modifier.modifierGroupID}`}
                                  className="btn btn-sm"
                                  title="Edit Modifier Group"
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '8px',
                                    border: 'none',
                                    backgroundColor: `${themeColor}15`,
                                    color: themeColor,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    cursor: 'pointer'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = themeColor;
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                                    e.currentTarget.style.boxShadow = `0 4px 12px ${themeColor}50`;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = `${themeColor}15`;
                                    e.currentTarget.style.color = themeColor;
                                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <i className="fa fa-edit" style={{ fontSize: '14px' }}></i>
                                </Link>
                              )}

                              {/* Assign to Items Icon
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedModifierGroupForAssignment(modifier);
                                  setShowItemAssignmentModal(true);
                                }}
                                className="btn btn-sm"
                                title="Assign to Items"
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '8px',
                                  border: 'none',
                                  backgroundColor: '#17a2b815',
                                  color: '#17a2b8',
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#17a2b8';
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(23, 162, 184, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#17a2b815';
                                  e.currentTarget.style.color = '#17a2b8';
                                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                }}
                              >
                                <i className="fa fa-plus" style={{ fontSize: '14px' }}></i>
                              </button>
 */}
                              {/* Delete Icon */}
                              {!isChildBranch && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(modifier);
                                  }}
                                  className="btn btn-sm"
                                  title="Delete Modifier Group"
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '8px',
                                    border: 'none',
                                    backgroundColor: '#dc354515',
                                    color: '#dc3545',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    cursor: 'pointer'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#dc3545';
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 53, 69, 0.4)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#dc354515';
                                    e.currentTarget.style.color = '#dc3545';
                                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                  }}
                                >
                                  <i className="fa fa-trash" style={{ fontSize: '14px' }}></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ItemAssignmentModal
        showModal={showItemAssignmentModal}
        setShowModal={setShowItemAssignmentModal}
        modifierGroup={selectedModifierGroupForAssignment}
        compID={CompID}
        onSuccess={() => {
          setShowItemAssignmentModal(false);
          setSelectedModifierGroupForAssignment(null);
        }}
      />

      <ItemAssignmentModal
        showModal={showBulkItemAssignmentModal}
        setShowModal={setShowBulkItemAssignmentModal}
        modifierGroups={Array.from(selectedItems)
          .map((id) =>
            modifierGroups.find((group) => group.modifierGroupID === id)
          )
          .filter(Boolean)}
        compID={CompID}
        isBulk={true}
        onSuccess={() => {
          setShowBulkItemAssignmentModal(false);
          setSelectedItems(new Set());
          lastSelectedIndex.current = null;
        }}
      />
    </div>
  );
}

export default ManageModifierGroups;
